import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { buildWhatsAppLink } from "../data/config";

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  city: "",
  model: "",
  buyerType: "",
  enquiryType: "",
  message: "",
  consent: false,
};

const clean = (value, fallback = "Not provided") => {
  const text = String(value || "").trim();
  return text || fallback;
};

const buildLeadWhatsAppMessage = (lead) =>
  [
    "Hi evLUXE, I am sharing a website enquiry.",
    "",
    `Name: ${clean(lead.name)}`,
    `Phone: ${clean(lead.phone)}`,
    `Email: ${clean(lead.email)}`,
    `City: ${clean(lead.city)}`,
    `Preferred Model: ${clean(lead.model)}`,
    `Buyer Type: ${clean(lead.buyerType)}`,
    `Enquiry Type: ${clean(lead.enquiryType)}`,
    `Message: ${clean(lead.message)}`,
    "",
    "Source: evLUXE website form",
  ].join("\n");

export const LeadForm = ({ products, content, prefill }) => {
  const section = content.formSection;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!prefill) return;
    setForm((f) => ({
      ...f,
      model: prefill.model ?? f.model,
      buyerType: prefill.buyerType ?? f.buyerType,
      enquiryType: prefill.enquiryType ?? f.enquiryType,
    }));
  }, [prefill]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = section.errors.name;
    const phone = form.phone.replace(/\s|-/g, "");
    if (!phone) e.phone = section.errors.phoneRequired;
    else if (!/^(\+91)?[6-9]\d{9}$/.test(phone))
      e.phone = section.errors.phoneInvalid;
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = section.errors.email;
    if (!form.city.trim()) e.city = section.errors.city;
    if (!form.consent) e.consent = section.errors.consent;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;

    const lead = { ...form, createdAt: new Date().toISOString() };
    const whatsappUrl = buildWhatsAppLink(
      buildLeadWhatsAppMessage(lead),
      content.contact.whatsappNumber
    );

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const inputCls =
    "bg-surface border-white/10 text-white placeholder:text-white/30 focus-visible:ring-brand focus-visible:ring-1 h-11 rounded-lg";

  return (
    <section id="register" data-testid="lead-form-section" className="relative py-24 lg:py-32 bg-surface-deep">
      <div className="absolute inset-0 grid-bg opacity-25" aria-hidden="true" />
      <div className="relative max-w-3xl mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="section-kicker justify-center mb-4">{section.kicker}</span>
          <h2 className="font-display font-bold text-4xl sm:text-5xl">{section.title}</h2>
          <p className="mt-5 text-white/60">
            {section.description}
          </p>
        </motion.div>

        <form
          onSubmit={handleSubmit}
          data-testid="lead-form"
          noValidate
          className="glass rounded-lg p-6 sm:p-9 space-y-5"
        >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="lf-name" className="text-white/80">{section.labels.fullName}</Label>
                <Input
                  id="lf-name"
                  data-testid="lf-name"
                  className={`mt-2 ${inputCls}`}
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder={section.placeholders.fullName}
                />
                {errors.name && <p className="text-destructive text-xs mt-1" data-testid="err-name">{errors.name}</p>}
              </div>
              <div>
                <Label htmlFor="lf-phone" className="text-white/80">{section.labels.phone}</Label>
                <Input
                  id="lf-phone"
                  data-testid="lf-phone"
                  className={`mt-2 ${inputCls}`}
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder={section.placeholders.phone}
                  inputMode="tel"
                />
                {errors.phone && <p className="text-destructive text-xs mt-1" data-testid="err-phone">{errors.phone}</p>}
              </div>
              <div>
                <Label htmlFor="lf-email" className="text-white/80">{section.labels.email}</Label>
                <Input
                  id="lf-email"
                  data-testid="lf-email"
                  className={`mt-2 ${inputCls}`}
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder={section.placeholders.email}
                  type="email"
                />
                {errors.email && <p className="text-destructive text-xs mt-1">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="lf-city" className="text-white/80">{section.labels.city}</Label>
                <Input
                  id="lf-city"
                  data-testid="lf-city"
                  className={`mt-2 ${inputCls}`}
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  placeholder={section.placeholders.city}
                />
                {errors.city && <p className="text-destructive text-xs mt-1" data-testid="err-city">{errors.city}</p>}
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-5">
              <div>
                <Label className="text-white/80">{section.labels.model}</Label>
                <Select value={form.model} onValueChange={(v) => set("model", v)}>
                  <SelectTrigger className={`mt-2 ${inputCls}`} data-testid="lf-model">
                    <SelectValue placeholder={section.placeholders.model} />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/80">{section.labels.buyerType}</Label>
                <Select value={form.buyerType} onValueChange={(v) => set("buyerType", v)}>
                  <SelectTrigger className={`mt-2 ${inputCls}`} data-testid="lf-buyer">
                    <SelectValue placeholder={section.placeholders.buyerType} />
                  </SelectTrigger>
                  <SelectContent>
                    {section.buyerTypes.map((b) => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/80">{section.labels.enquiryType}</Label>
                <Select value={form.enquiryType} onValueChange={(v) => set("enquiryType", v)}>
                  <SelectTrigger className={`mt-2 ${inputCls}`} data-testid="lf-enquiry">
                    <SelectValue placeholder={section.placeholders.enquiryType} />
                  </SelectTrigger>
                  <SelectContent>
                    {section.enquiryTypes.map((e) => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="lf-message" className="text-white/80">{section.labels.message}</Label>
              <Textarea
                id="lf-message"
                data-testid="lf-message"
                className="mt-2 bg-surface border-white/10 text-white placeholder:text-white/30 focus-visible:ring-brand focus-visible:ring-1 rounded-lg min-h-[100px]"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                placeholder={section.placeholders.message}
              />
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="lf-consent"
                data-testid="lf-consent"
                checked={form.consent}
                onCheckedChange={(v) => set("consent", v === true)}
                className="mt-1 border-white/30 data-[state=checked]:bg-brand data-[state=checked]:border-brand"
              />
              <Label htmlFor="lf-consent" className="text-white/65 text-sm leading-relaxed cursor-pointer">
                {section.consent}
              </Label>
            </div>
            {errors.consent && <p className="text-destructive text-xs -mt-2" data-testid="err-consent">{errors.consent}</p>}

            <div className="pt-2">
              <button
                type="submit"
                data-testid="lead-submit"
                className="btn-glow inline-flex w-full items-center justify-center gap-2 bg-brand text-black font-semibold px-7 py-3.5 rounded-full"
              >
                <MessageCircle size={18} /> Send Enquiry on WhatsApp
              </button>
              <p className="mt-3 text-center text-xs text-white/45">
                WhatsApp will open with your enquiry filled in. Tap Send in WhatsApp to share it with evLUXE.
              </p>
            </div>
        </form>
      </div>
    </section>
  );
};

export default LeadForm;
